import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Button, Layout } from "antd";
import { EditableTable } from "../../components/editable-table/EditableTable";
import { BASE_PATH } from "../../api/base";
import { FolderOutlined } from '@ant-design/icons';
import { fetchFilesByIdsAsync } from "../files/FilesEntitySlice";
import { createRopCalculationCableAsync, deleteRopCalculationCableAsync, fetchRopCalculationsCableByQuarterAsync, selectRopCalculationsCable, updateRopCalculationCableAsync } from "./RopCalculationsCableSlice";
import { RopCalculationCable } from "../../api/models/rop-calculation-cable";
import { UpdateRopCalculationsCableDto } from "../../api/models/update-rop-calculations-cable-dto";
import { CreateRopCalculationsCableDto } from "../../api/models/create-rop-calculations-cable-dto";
import { RopCalculationsCableForm } from "./components/RopCalculationsCableForm";
import { RopCalculationsCableColumns } from "./form/column";
import { FileForm } from "../files/components/FileForm";
import { selectOrganizationsProfile } from "../organizationProfile/organizationProfileSlice";

interface RopCalculationCablesComponentProps {
    organizationId?: string,
    year: number;
    quarter: number;
}

export const RopCalculationsCableComponent: React.FC<RopCalculationCablesComponentProps> = ({ organizationId, year, quarter }) => {
    const dispatch = useAppDispatch();
    const calculations: RopCalculationCable[] = useAppSelector(selectRopCalculationsCable);
    const organization = useAppSelector(selectOrganizationsProfile);
    
    const updatedColumns: (RopCalculationCable & { bin?: string; organizationName?: string })[] = calculations.map((column) => ({
        ...column,
        bin: organization?.bin, 
        organizationName: organization?.name 
      }));

    useEffect(() => {
        if (organizationId) {
            if (year && quarter) {
                dispatch(fetchRopCalculationsCableByQuarterAsync({ organizationId, year, quarter }));
            } else {
                dispatch(fetchRopCalculationsCableByQuarterAsync({ organizationId, year: new Date().getFullYear(), quarter: Math.ceil(new Date().getMonth() / 3) }));
            }
        }
    }, [year, quarter, organizationId, dispatch]);

    const onUpdate = async (ropCalculation: UpdateRopCalculationsCableDto, id: string) => {
        return dispatch(updateRopCalculationCableAsync({ ropCalculation, id })).then((res: any) => {
            if (!res.error) {
                if (organizationId) {
                    if (year && quarter) {
                        dispatch(fetchRopCalculationsCableByQuarterAsync({ organizationId, year, quarter }));
                    } else {
                        dispatch(fetchRopCalculationsCableByQuarterAsync({ organizationId, year: new Date().getFullYear(), quarter: Math.ceil(new Date().getMonth() / 3) }));
                    }
                }
                return true;
            }
            return false;
        });
    };

    const onCreateRopCalculation = (ropCalculation: CreateRopCalculationsCableDto) => {
        if (!organizationId || !year || !quarter) return;

        dispatch(createRopCalculationCableAsync({ ...ropCalculation, organizationId, year, quarter })).then((res: any) => {
            if (!res.error) {
                setFormVisible(false);
            }
        });
    };

    const deleteRopCalculation = (id: string) => {
        return dispatch(deleteRopCalculationCableAsync({ id })).then((res: any) => {
            if (!res.error) {
                return true;
            }
            return false;
        });
    }

    const [visibleForm, setFormVisible] = useState(false);

    const addButton = (
        <Button onClick={() => setFormVisible(true)} block type="dashed" className="">
            + Добавить запись
        </Button>)

    const [visible, setVisible] = useState(false);
    const [ropCalculation, setCurrentRopCalculation] = useState<RopCalculationCable>();

    const showFileModal = async (calculation: RopCalculationCable) => {
        // Fetch files for the current calculation
        if (calculation.fileIds?.length > 0) {
            await dispatch(fetchFilesByIdsAsync({ ids: calculation.fileIds.join(',') }));
        }

        setCurrentRopCalculation(calculation);
        setVisible(true);
    };

    return (
        <Layout className="bg-white p-4 rounded-lg">
            {visibleForm && <RopCalculationsCableForm visible={visibleForm} setVisible={setFormVisible} onCreateRopCalculationsCable={onCreateRopCalculation} organizationId={organizationId} />}
            <div className="table-responsive">
                <EditableTable<RopCalculationCable, UpdateRopCalculationsCableDto>
                    onSave={(value, id) => onUpdate(value, id)}
                    onDelete={(calculation) => deleteRopCalculation(calculation.id)}
                    entityColumns={[
                        {
                            title: "Файлы",
                            dataIndex: "btnc" as keyof RopCalculationCable,
                            key: "btnc",
                            inputNode: <></>,
                            render: (value: any, record: RopCalculationCable, index: any) => (
                                 <Button.Group>
                                    <Button icon={<FolderOutlined />} onClick={() => showFileModal(record)}></Button>
                                </Button.Group>
                            )
                            ,
                            editable: false,
                            copyable: false,
                        },
                        ...RopCalculationsCableColumns(),
                    ]}
                    dataSource={updatedColumns}
                    footer={() => addButton}
                />
            </div>
            <Button href={`${BASE_PATH}/rop-calculations-cable/report/organization/${organizationId}/year/${year}/quarter/${quarter}`} block type="primary" ghost>
                Получить отчёт
            </Button>
            {visible && <FileForm visible={visible} setVisible={setVisible} onUpdate={onUpdate} fileIds={ropCalculation?.fileIds} id={ropCalculation?.id} />}
        </Layout>
    );

}